// Purpose: to aid in unsubscribing from pubsub-js
// Tracks: pubsub-js topics & identifying values (token: pubSubToken, function:subscriptionHandler, customTracker:owner)

const isDefined = v => v !== undefined && v !== null;

class TopicManager {
    constructor() {
        /**
         * Internal registry of topics.
         * Structure:
         * {
         *   [topicName]: [
         *      { token, subscriptionHandler, owner },
         *      ...
         *   ]
         * }
         */
        this._topicMetas = {};
    }





    /* ************************
     * ************************
     *  MAIN
     * ************************
     * ************************/

    /**
     * Registers a subscriber (token, handler, owner) to a given topic.
     * @param {string} topic - The topic name
     * @param {Object} param1
     * @param {string} param1.token - The PubSub subscription token
     * @param {Function} param1.subscriptionHandler - The handler function for this subscriber - format: (topic, data) => { //DO_STUFF }
     * @param {any} param1.owner - An identifier to track the subscription owner
     */
    joinTopic = (topic, { token: pubSubToken, subscriptionHandler, owner }) => {
        if (!this.hasOpenTopic(topic)) {
            this._openTopic(topic)
        }
        this._topicMetas[topic].push({ token: pubSubToken, subscriptionHandler, owner });
    }


    /**
     * Removes a subscriber from a topic based on token, handler, or owner.
     * At least one of these must be provided - token, subscriptionHandler, owner
     * @param {string} topic - The topic name
     * @param {Object} [param1={}]
     * @param {string} [param1.token] - Subscription token
     * @param {Function} [param1.subscriptionHandler] - Handler function
     * @param {any} [param1.owner] - Owner identifier
     */
    leaveTopic = (topic, { token: pubSubToken, subscriptionHandler, owner } = {}) => {
        if (!isDefined(pubSubToken) && !isDefined(subscriptionHandler) && !isDefined(owner)) {
            throw new Error('TopicManager: method .leaveTopic() requires either "token" OR "subscriptionHandler" OR "owner"');
        }

        const filterOutByToken = !isDefined(pubSubToken) ? undefined : (meta) => meta.token !== pubSubToken;
        const filterOutBySubscriptionHandler = !isDefined(subscriptionHandler) ? undefined : (meta) => meta.subscriptionHandler !== subscriptionHandler;
        const filterOutByOwner = !isDefined(owner) ? undefined : (meta) => meta.owner !== owner;

        const filter = filterOutByToken || filterOutBySubscriptionHandler || filterOutByOwner;

        let topicTopicMetas = this.listTopicTopicMetas(topic).filter(filter);

        this._topicMetas[topic] = topicTopicMetas;

        if (topicTopicMetas.length === 0) {
            this._closeTopic(topic);
        }
    }


    /**
     * Clears all topics and subscriptions.
     */
    clear = () => {
        this._topicMetas = {};
    }





    /* ************************
     * ************************
     *  LIST
     * ************************
     * ************************/


    /**
     * Returns a list of all tracked topics.
     * @returns {string[]}
     */    
    listTopics = () => {
        return Object.keys(this._topicMetas);
    }


    /**
     * Returns all subscriber meta objects for a topic.
     * @param {string} topic
     * @returns {Array<{token:string, subscriptionHandler:Function, owner:any}>}
     */
    listTopicTopicMetas = (topic) => {
        return (this._topicMetas[topic] || []);
    }

    /**
     * Returns all subscription tokens for a topic.
     * @param {string} topic
     * @returns {string[]}
     */
    listTopicTokens = (topic) => {
        return this.listTopicTopicMetas(topic).map(({ token } = {}) => token);
    }

    /**
     * Returns all subscription handlers for a topic.
     * @param {string} topic
     * @returns {Function[]} - format: (topic, data) => { //DO_STUFF }
     */
    listTopicSubscriptionHandlers = (topic) => {
        return this.listTopicTopicMetas(topic).map(({ subscriptionHandler } = {}) => subscriptionHandler);
    }

    /**
     * Returns all owners for a topic.
     * @param {string} topic
     * @returns {any[]}
     */
    listTopicOwners = (topic) => {
        return this.listTopicTopicMetas(topic).map(({ owner } = {}) => owner);
    }





    /* ************************
     * ************************
     *  INTROSPECT
     * ************************
     * ************************/

     /**
     * Retrieves a single subscription meta object for a topic,
     * matched by token, handler, or owner. - at least one is required.
     * @param {string} topic
     * @param {Object} [param1={}]
     * @param {string} [param1.token]
     * @param {Function} [param1.subscriptionHandler]
     * @param {any} [param1.owner]
     * @returns {{token:string, subscriptionHandler:Function, owner:any}|undefined}
     */
    getTopicMeta = (topic, { token: pubSubToken, subscriptionHandler, owner } = {}) => {
        if (!isDefined(pubSubToken) || !isDefined(subscriptionHandler) || !isDefined(owner)) {
            throw new Error('TopicManager: method .getTopicMeta() requires either "token" OR "subscriptionHandler" OR "owner"');
        }

        const matchesOnToken = !isDefined(pubSubToken) ? undefined : (meta) => meta.token === pubSubToken;
        const matchesOnSubscriptionHandler = !isDefined(subscriptionHandler) ? undefined : (meta) => meta.subscriptionHandler === subscriptionHandler;
        const matchesOnOwner = !isDefined(owner) ? undefined : (meta) => meta.owner === owner;

        const matches = matchesOnToken || matchesOnSubscriptionHandler || matchesOnOwner;

        return this.listTopicTopicMetas(topic).find(topicMeta => matches(topicMeta));
    }


    /**
     * @returns {boolean} true if there are any active topics.
     */
    hasOpenTopics = () => {
        return this.listTopics().length > 0;
    }

    /**
     * @param {string} topic
     * @returns {boolean} true if the given topic exists in registry.
     */
    hasOpenTopic = (topic) => {
        return !!this._topicMetas[topic];
    }





    /* ************************
     * ************************
     *  PRIVATE
     * ************************
     * ************************/

    /**
     * Initializes a topic in the registry.
     * @param {string} topic
     * @private
     */
    _openTopic = (topic) => {
        this._topicMetas[topic] = [];
    }

    /**
     * Removes a topic from registry, only if it has no subscribers.
     * @param {string} topic
     * @private
     */
    _closeTopic = (topic) => {
        const hasTopicMembers = this.listTopicTopicMetas(topic).length > 0;
        if (hasTopicMembers) {
            throw new Error('TopicManager: Unable to close topic because it still has members.')
        }
        delete this._topicMetas[topic];
    }





    /* ************************
     * ************************
     *  DEBUGGING
     * ************************
     * ************************/

    printState() {
        const json = {
            state: this._topicMetas,
            meta: {
                hasOpenTopics: this.hasOpenTopics(),
                listTopics: this.listTopics(),
                listTopicTokens: this.listTopicTokens(),
                listTopicSubscriptionHandlers: this.listTopicSubscriptionHandlers().map(v => !!v),
                listTopicOwners: this.listTopicOwners(),
            }
        }
        return json;
    }


    consoleLogState() {
        console.log('ps: tm:', '******************************');
        console.log('ps: tm:', '*****************************');
        console.log('ps: tm:', '******************************');
        console.log('ps: tm:', 'PubSubWebsocketMetal.state');
        console.log('ps: tm:', this.printState());
    }

}

export default TopicManager;